import React, { useState } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping';
import SuspectEmailCaptureFormSection from './components/suspectEmailCaptureFormSection';
import UnderstandingProduct from './components/understandingProduct';
// images
import desktopImageWebp from './components/heroSection/images/life-insurance-hero.webp';
import mobileImageWebp from './components/heroSection/images/life-insurance-hero-mobile.webp';
// dislaimer footnotes
import {
  DividendsAreNotGuaranteed,
  HighestFinancialStrengthRatings,
  NerdWalletAllRightsReserved,
  YourPolicysCashValue,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Some clients will need additional underwriting requirements.
        </>,
        <>
          <sup>2</sup>&nbsp;Assumes that all premiums are paid. The amount of your payments will directly affect your policy&#39;s cash value, as well as your ability to maintain coverage in the future.
        </>,
        <>
          {YourPolicysCashValue(3)}
        </>,
        <>
          <sup>4</sup>&nbsp;Subject to age, insurability requirements, limits mandated by tax code and a minimum cash value to cover policy expenses.
        </>,
        <>
          <sup>5</sup>&nbsp;All investments carry some level of risk. No investment strategy can guarantee a profit or protect against a loss.
        </>,
        <>
          {DividendsAreNotGuaranteed(6)}
        </>,
        <>
          {NerdWalletAllRightsReserved(7)}
        </>,
        <>
          {HighestFinancialStrengthRatings(8)}
        </>,
        <>
          To be used with form numbers ICC12.TT.TERM.(0513), ICC12.TT.TERM.L20.(0513), ICC21.UU.WL.(1121), ICC17.UU.WL.(0119), ICC18.UU.CUL.(0919), UU.VULP.(1019), ICC18.UU.ACB.(0119), ICC17.UU.WL.WP.(0119), ICC17.UU.APB.(0119) or state equivalent.
        </>,
      ]}
      disableDisclaimerTopBorder
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Life Insurance',
          linkUrl: '/life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Life Insurance',
        },
      ]}
      stateFilingComplianceNumber={<>90&#45;2723&#45;91 (0824)</>}
    >

      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={mobileImageWebp} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={desktopImageWebp} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={desktopImageWebp}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      {/* LIFE INSURANCE CALCULATOR */}
      <ExperienceCallout />

      <UnderstandingProduct />

      <FinancialPlanningSection />

      <ProductCalloutGrouping />

      <SuspectEmailCaptureFormSection
        config={config}
        loadRecaptcha={loadRecaptcha}
        setLoadRecaptcha={setLoadRecaptcha}
      />

      <Faq />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText={<>Let&#39;s boost your life insurance knowledge</>}
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
